import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";

function BrandLogo() {
  // Iter 162 — Horizontal transparent PNG lockup at /public/logo.png.
  // Height-constrained (h-9 mobile / h-10 desktop) so the 64px header
  // rhythm stays intact and there's no layout shift on load. Aspect
  // ratio 1382:266 ≈ 5.20:1 → renders at 36×187 (mobile) / 40×208
  // (desktop). Cropped from the original 2000×2000 source asset to its
  // content bounds so no invisible padding steals horizontal space.
  // Cache-busted (?v=162) to force browsers past the previous logo.
  return (
    <img
      src="/logo.png?v=162"
      alt="VerifiedGamblers"
      className="h-9 sm:h-10 w-auto object-contain select-none"
      draggable="false"
      data-testid="brand-logo"
    />
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();

  const dashPath = user?.role === "admin" ? "/admin" : user?.role === "streamer" ? "/streamer" : user?.role === "player" ? "/player" : "/";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0E14]/70 border-b border-white/8">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-home" className="flex items-center group shrink-0">
          <BrandLogo />
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          {/* Iter 169 — Public "How to use VG" link surfaced in the top
              nav for new visitors. Rendered before login/dashboard so
              logged-out users see it first; also visible to logged-in
              users (harmless — same public content). */}
          <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex rounded-none text-[#F5B841] hover:text-[#F5B841] hover:bg-white/10 hover:underline underline-offset-4 h-9 px-3 text-base font-semibold transition-colors">
            <Link to="/how-it-works" data-testid="nav-how-it-works">{t("nav.howItWorks")}</Link>
          </Button>
          <LanguageSwitcher />
          {user ? (
            <>
              {(user.role === "admin" || (user.role === "streamer" && user.status === "verified")) && (
                <Button asChild variant="ghost" size="sm" className="rounded-none text-[#E8ECF4] hover:bg-white/10 h-9 px-3 font-medium transition-colors">
                  <Link to="/marketplace" data-testid="nav-marketplace">Marketplace</Link>
                </Button>
              )}
              <Button asChild variant="ghost" size="sm" className="rounded-none text-[#E8ECF4] hover:bg-white/10 h-9 px-3 font-medium transition-colors">
                <Link to={dashPath} data-testid="nav-dashboard">{t("nav.dashboard")}</Link>
              </Button>
              <Button
                data-testid="nav-logout"
                onClick={async () => { await logout(); nav("/"); }}
                size="sm"
                className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-9 px-4 font-semibold transition-colors"
              >
                {t("nav.logout")}
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="rounded-none text-[#E8ECF4] hover:bg-white/10 h-9 px-3 font-medium transition-colors">
                <Link to="/login" data-testid="nav-login">{t("nav.login")}</Link>
              </Button>
              <Button asChild size="sm" className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-9 px-4 font-semibold transition-colors">
                <Link to="/register" data-testid="nav-register">{t("nav.register")}</Link>
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
